/**
 * Auth slice using Zustand - Persisted to localStorage via main store
 * Refactored from the original auth store to follow the slice pattern
 */

import type { StateCreator } from 'zustand';
import type { AuthSlice, AppStore } from '../types';
import { env } from '@/lib/env';

export const createAuthSlice: StateCreator<AppStore, [], [], AuthSlice> = (set, get) => ({
  // Initial state - all in memory only (no refresh token stored)
  user: null,
  accessToken: null,
  tokenExpiry: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading true until auth check completes

  // Actions
  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
    }),

  setAccessToken: (accessToken) =>
    set({
      accessToken,
    }),

  setTokenExpiry: (tokenExpiry) =>
    set({
      tokenExpiry,
    }),

  setLoading: (isLoading) =>
    set({
      isLoading,
    }),

  login: (user, accessToken, expiresIn) => {
    const tokenExpiry = expiresIn ? Date.now() + expiresIn * 1000 : null;

    set({
      user,
      accessToken,
      tokenExpiry,
      isAuthenticated: true,
      isLoading: false,
    });

    // Clear workspace data on new login to ensure fresh state
    get().clearWorkspaceData();
  },

  logout: async () => {
    // Navigate directly without clearing state first
    // Clearing state triggers auth guards which cancel the navigation
    // Backend will clear cookies, and state will be cleared on next page load
    // The browser will naturally follow the redirect chain:
    // 1. Backend returns 302 to WorkOS logout URL (with cookie clearing)
    // 2. Browser follows → WorkOS clears session
    // 3. WorkOS returns 302 to /logout-success
    // 4. Browser follows → User sees logout page (AuthProvider skips refresh)
    window.location.href = `${env.api.url}/auth/logout`;
  },

  updateUser: (updates) => {
    const currentUser = get().user;
    if (!currentUser) return;

    set({
      user: { ...currentUser, ...updates },
    });
  },

  updateTokens: (accessToken, expiresIn) => {
    const tokenExpiry = expiresIn ? Date.now() + expiresIn * 1000 : null;

    set({
      accessToken,
      tokenExpiry,
    });
  },

  isTokenExpired: () => {
    const { tokenExpiry } = get();
    if (!tokenExpiry) return false;
    return Date.now() > tokenExpiry;
  },
});
